import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { ButtonDisabledOrLoader } from '../../../../../../../../../../baseComponents/Button'
import PermissionChecker from '../../../../../../../../../../baseComponents/PermissionChecker'
import { TwinIconToolTip } from '../../../../../../../../../../baseComponents/TwinIcon'
import { AdjustmentDownloadButtonCSV, AdjustmentPdfPreview, AdjustmentDownloadPdfTemplateButton, AdjustmentDownloadButtonPDF } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderAdjustmentDeliveryNoteLayout/AdjustmentButtons'
import { ButtonOrderInfoLeftBase, ButtonOrderInfoLeftBaseProps, OrderProductTypesButton } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import { checkBuyDeliveryNoteAllOk } from '../../../../../../../../../../utils/globals/buy/buy'
import twinFetchPost from '../../../../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../../../../utils/globals/link'
import useEditDeleteModal from '../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useIsLoading from '../../../../../../../../../../utils/hooks/useIsLoading'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import { changeErrorMessage } from '../../../../../../../../../../utils/reducers/reduxDispatch'
import ModalDeleteAdjustmentDeliveryNoteDraft from './ModalDeleteAdjustmentDeliveryNoteDraft'

const AdjustmentDeliveryNoteCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageAdjustment } = useAdjustmentDeliveryNoteCreateStepButtonsLogic({ data, onSubmit })
    const OrderId = data?.header?.id
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useAdjustmentDeliveryNoteCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <AdjustmentDeliveryNoteCreateButton  title={t('processAdjustmentDeliveryNote', 'Procesar albarán de regularización')} disabled={infoMessageAdjustment ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageAdjustment || ''} />
        </div>
    )
}

const useAdjustmentDeliveryNoteCreateStepButtonsLogic = ({ data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const Order = data?.header
    const name = 'adjustment_delivery_note_create_step_buttons'
    const userInfo = getUserInfo()
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = Order?.CreateEmployeeId
    const isMyAdjustment = orderEmployeeId === EmployeeId
    const hideDeleteButton = !isMyAdjustment
    let infoMessageAdjustment = checkBuyDeliveryNoteAllOk(data, t)

    const defaultButtons = [{ "id": "previewAdjustmentDeliveryNotePDF" }, { "id": "downloadAdjustmentDeliveryNote" }, { "id": "duplicateAdjustmentDeliveryNote" }, { "id": "deleteAdjustmentDeliveryNote" }]

    const adjustmentDeliveryNoteDeleteText = t('deleteAdjustmentDeliveryNoteDraft', 'Eliminar borrador albarán de regularización')
    const adjustmentDeliveryNoteDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const adjustmentDeliveryNotePreviewPDFText = t('previewPdfTemplate', 'Previsualizar plantilla PDF')
    const adjustmentDeliveryNoteDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const adjustmentDeliveryNoteDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const adjustmentOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            deleteAdjustmentDeliveryNote: {
                component: AdjustmentDeliveryNoteDeleteButton,
                showButton: !hideDeleteButton,
                extraProps: {
                    title: adjustmentDeliveryNoteDeleteText,
                    id: 'deleteAdjustmentDeliveryNote',
                    allRowData: data,
                }
            },
            downloadAdjustmentCsv: {
                component: AdjustmentDownloadButtonCSV,
                extraProps: {
                    title: adjustmentDeliveryNoteDownloadCSVText,
                    id: 'downloadAdjustmentCsv',
                    Order,
                    typeTemplate: 'adjustmentDeliveryNote'
                }
            },
            previewAdjustmentDeliveryNotePDF: {
                component: AdjustmentPdfPreview,
                extraProps: {
                    title: adjustmentDeliveryNotePreviewPDFText,
                    id: 'previewAdjustmentDeliveryNotePDF',
                    key: 'previewAdjustmentDeliveryNotePDF',
                    Order,
                    typeTemplate: 'adjustmentDeliveryNote'
                }
            },
            downloadAdjustmentDeliveryNoteTemplate: {
                component: AdjustmentDownloadPdfTemplateButton,
                extraProps: {
                    title: adjustmentDeliveryNoteDownloadPDFTemplateText,
                    id: 'downloadAdjustmentDeliveryNoteTemplate',
                    key: 'downloadAdjustmentDeliveryNoteTemplate',
                    Order,
                    typeTemplate: 'adjustmentDeliveryNote'
                }
            },
            downloadAdjustmentDeliveryNote: {
                component: AdjustmentDownloadButtonPDF,
                extraProps: {
                    title: adjustmentDeliveryNoteDownloadPDFText,
                    id: 'downloadAdjustmentDeliveryNote',
                    Order,
                    typeTemplate: 'adjustmentDeliveryNote'
                }
            },
            adjustmentProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: adjustmentOrderProductTypesText,
                    id: 'adjustmentProductTypes',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData,
                }
            },
        }
    }, [Order, data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions, adjustmentDeliveryNoteDeleteText, adjustmentDeliveryNoteDownloadCSVText, adjustmentDeliveryNoteDownloadPDFTemplateText, adjustmentDeliveryNoteDownloadPDFText, adjustmentDeliveryNotePreviewPDFText, hideDeleteButton, adjustmentOrderProductTypesText])

    return { infoMessageAdjustment, name, ButtonComponents, defaultButtons }
}

export const AdjustmentDeliveryNoteCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageAdjustment } = useAdjustmentDeliveryNoteCreateStepButtonsLogic({ data, onSubmit })
    const OrderId = data?.header?.id
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useAdjustmentDeliveryNoteCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <AdjustmentDeliveryNoteCreateButton title={t('processAdjustmentDeliveryNote', 'Procesar albarán de regularización')} disabled={infoMessageAdjustment ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageAdjustment || ''} />
        </div>
    )
}

type AdjustmentDeliveryNoteCreateButtonProps = AdjustmentDeliveryNoteCreateButtonLogicProps & ButtonOrderInfoLeftBaseProps & {
    title: string
    children?: React.ReactNode
}

const AdjustmentDeliveryNoteCreateButton: React.FC<AdjustmentDeliveryNoteCreateButtonProps> = ({ title, OrderId, onSubmit, status, disabled, infoMessage, ...rest }) => {
    const { nextStepDeliveryNote, loading } = useAdjustmentDeliveryNoteCreateButtonLogic({ OrderId, onSubmit, status })
    return (
        <ButtonOrderInfoLeftBase disabled={disabled} infoMessage={infoMessage} {...rest}>
            <div className='min-w-175'>
                <ButtonDisabledOrLoader onClick={nextStepDeliveryNote} buttonIsDisabled={disabled} textButton={title} loading={loading} className='w-full'/>
            </div>
        </ButtonOrderInfoLeftBase>
    )
}

interface AdjustmentDeliveryNoteCreateButtonLogicProps {
    onSubmit: () => void
    OrderId?: string
    status: number
}

const useAdjustmentDeliveryNoteCreateButtonLogic = ({ onSubmit, OrderId, status }: AdjustmentDeliveryNoteCreateButtonLogicProps) => {
    const {t} = useTwinTranslation()
    const errorUniqueNumberNLine = t('errorWithTheUniqueNumberNLine', 'Existe un error con el número de serie de la línea')
    const errorQuantityUniqueNumberNLine = t('errorQuantityWithTheUniqueNumberNLine', 'Existe un error con la cantidad del número de serie de la línea')
    const duplicateUniqueNumber = t('alreadyExistsUniqueNumber', 'Ya existe un producto con el número de serie')
    const noWarehouses = t('noWarehousesInWorkingCenter', 'Este centro de trabajo no tiene ningún almacén asociado')
    const { startLoading, endLoading, loading } = useIsLoading()

    const nextStepDeliveryNote = useCallback(async () => {
        startLoading?.()
        const response = await twinFetchPost('/api/app/order/adjustmentDeliveryNote/updateAdjustmentDeliveryNote', { id: OrderId, status: status + 1 })
        if (response.status === 200) {
            onSubmit()
        } else {
            const responseJSON = await response.json()
            const error = responseJSON.errors[0].msg
            const splitError = error.split('-')
            if (splitError[0] === 'This serial number already exists') {
                changeErrorMessage(duplicateUniqueNumber + ' ' + splitError[1])
            } else if (splitError[0] === 'Serial Number is required') {
                changeErrorMessage(errorUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'This serial number has quantity problem'){
                changeErrorMessage(errorQuantityUniqueNumberNLine + ' ' + splitError[1])
            } else if (splitError[0] === 'noWarehouses') {
                changeErrorMessage(noWarehouses)
            }
        }
        endLoading?.()
    }, [OrderId, onSubmit, status, errorUniqueNumberNLine, errorQuantityUniqueNumberNLine, duplicateUniqueNumber, noWarehouses, startLoading, endLoading])

    return { nextStepDeliveryNote, loading }
}

interface AdjustmentDeliveryNoteDeleteButtonProps {
    allRowData: OrderData
    title: string
    id: string
    children?: React.ReactNode
}

const AdjustmentDeliveryNoteDeleteButton: React.FC<AdjustmentDeliveryNoteDeleteButtonProps> = ({ allRowData, id, title, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useAdjustmentDeliveryNoteDeleteButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('adjustmentDeliveryNote')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteAdjustmentDeliveryNoteDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit} /> : null}
        </PermissionChecker>
)
}

const useAdjustmentDeliveryNoteDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'adjustmentDeliveryNotes')

    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}

export default AdjustmentDeliveryNoteCreateStepButtons